import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import * as XLSX from "xlsx";
import { useSnackbar } from "notistack";
import { RiderServiceTicket, sparePartsService } from "../../services/sparePartsService";
import { hasAnyRole } from '../../utils/roleUtils';

interface RiderServiceTicketsTabProps {
  riderId: string;
  riderDatabaseId: string;
}

const formatDate = (date?: string | null) =>
  date ? new Date(date).toLocaleString("en-IN") : "-";

const formatAmount = (amount?: number | string | null) => {
  if (amount === null || amount === undefined || amount === "") return "-";
  const value = Number(amount);
  return Number.isFinite(value)
    ? value.toLocaleString("en-IN", { style: "currency", currency: "INR" })
    : String(amount);
};

const API_BASE_URL = (import.meta.env.VITE_API_URL || "/api").replace(/\/+$/, "");

const RiderServiceTicketsTab: React.FC<RiderServiceTicketsTabProps> = ({
  riderId,
  riderDatabaseId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const canExport = hasAnyRole([
    'Super Admin',
    'Supply Admin',
    'Supply User'
  ]);
  const [subTab, setSubTab] = useState(0);
  const [rows, setRows] = useState<RiderServiceTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const fetchTickets = useCallback(async () => {
    if (!riderId) return;
    setLoading(true);
    setError("");
    try {
      const response = await sparePartsService.getRiderServiceTickets({
        riderId,
        ...(subTab === 0 ? { tickets: "all" as const } : {}),
      });
      setRows(response.data ?? []);
      setTotal(response.data?.length ?? 0);
    } catch (requestError) {
      console.error("Failed to fetch rider service tickets:", requestError);
      setRows([]);
      setTotal(0);
      setError("Unable to load service tickets. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [riderId, subTab]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleSubTabChange = (_event: React.SyntheticEvent, value: number) => {
    setSubTab(value);
    setPage(0);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleExport = async () => {
    if (!canExport || !riderId) return;

    setExporting(true);
    try {
      const params = {
        riderId,
        ...(subTab === 0 ? { tickets: "all" as const } : {}),
      };
      const response = await sparePartsService.getRiderServiceTickets(params);
      const tickets = response.data ?? [];

      if (tickets.length === 0) {
        enqueueSnackbar("There are no service tickets to export.", { variant: "info" });
        return;
      }

      const exportRows = tickets.map((ticket) => ({
        "Ticket": ticket.code,
        "Service Type": ticket.serviceType.replace(/_/g, " "),
        "Status": ticket.status.replace(/_/g, " "),
        "Rider ID": ticket.riderId || riderId,
        "City": ticket.city || "",
        "Client": ticket.clientName || "",
        "Client Rider ID": ticket.clientRiderID || "",
        "Vehicle ID": ticket.vehicleId || "",
        "Opened At": ticket.openedAt ? formatDate(ticket.openedAt) : "",
        "Closed At": ticket.closedAt ? formatDate(ticket.closedAt) : "",
        "Repair Cost": ticket.actualRepairCost ?? "",
        "Collected": ticket.actualRepairCostCollected ?? "",
        ...(subTab === 1
          ? {
              "Pending Amount": ticket.actualRepairCost == null
                ? ""
                : Number(ticket.actualRepairCost) -
                  Number(ticket.actualRepairCostCollected ?? 0),
            }
          : {}),
      }));
      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Service Tickets");
      const safeRiderId = riderId.replace(/[^a-zA-Z0-9_-]/g, "_");
      XLSX.writeFile(
        workbook,
        `service-tickets-${safeRiderId}-${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (requestError) {
      console.error("Failed to export rider service tickets:", requestError);
      enqueueSnackbar("Unable to export service tickets. Please try again.", {
        variant: "error",
      });
    } finally {
      setExporting(false);
    }
  };

  const closeCreateDialog = () => {
    if (creating) return;
    setCreateDialogOpen(false);
    setTitle("");
    setDescription("");
    setImage(null);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const visibleRows = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handleCreateTicket = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!riderDatabaseId || !image || !title.trim() || !description.trim()) {
      enqueueSnackbar("Title, description, and an image are required.", { variant: "error" });
      return;
    }

    setConfirmDialogOpen(true);
  };

  const submitConfirmedTicket = async () => {
    if (!riderDatabaseId || !image) return;

    const formData = new FormData();
    formData.append("riderId", riderDatabaseId);
    formData.append("title", title.trim());
    formData.append("description", description.trim());
    formData.append("image", image);

    setCreating(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/additional-apis-withToken/raise-service-ticket`,
        { method: "POST", body: formData }
      );
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to raise service ticket.");
      }

      enqueueSnackbar(result.message || "Service ticket raised successfully.", {
        variant: "success",
      });
      setConfirmDialogOpen(false);
      setCreateDialogOpen(false);
      setTitle("");
      setDescription("");
      setImage(null);
      if (imageInputRef.current) imageInputRef.current.value = "";
      await fetchTickets();
    } catch (requestError) {
      console.error("Failed to raise rider service ticket:", requestError);
      enqueueSnackbar(
        requestError instanceof Error ? requestError.message : "Unable to raise service ticket.",
        { variant: "error" }
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Tabs
          value={subTab}
          onChange={handleSubTabChange}
          aria-label="service ticket views"
          sx={{
            p: 0.5,
            minHeight: 0,
            width: "fit-content",
            maxWidth: "100%",
            borderRadius: 2.5,
            bgcolor: "action.hover",
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTabs-flexContainer": { gap: 0.75 },
            "& .MuiTab-root": {
              minHeight: 44,
              minWidth: { xs: 130, sm: 170 },
              px: { xs: 1.25, sm: 2 },
              borderRadius: 2,
              color: "text.secondary",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 700,
              transition: "background-color 160ms ease, color 160ms ease, box-shadow 160ms ease",
              "&:hover": { bgcolor: "background.paper", color: "text.primary" },
              "&.Mui-selected": {
                bgcolor: "background.paper",
                color: "primary.main",
                boxShadow: 2,
              },
              "& .MuiTab-iconWrapper": { mr: 1, mb: "0 !important" },
            },
          }}
        >
          <Tab
            icon={<BuildOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label="All Tickets"
          />
          <Tab
            icon={<PaymentsOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label="Pending Collection Amount"
          />
        </Tabs>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={18} /> : <DownloadOutlinedIcon />}
            onClick={handleExport}
            disabled={!canExport || loading || exporting}
            sx={{ minHeight: 44, whiteSpace: "nowrap" }}
          >
            {exporting ? "Exporting..." : "Export"}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => setCreateDialogOpen(true)}
            disabled={!canExport || !riderDatabaseId}
            sx={{ minHeight: 44, whiteSpace: "nowrap" }}
          >
            Raise Service Ticket
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, whiteSpace: "nowrap" } }}>
                    <TableCell>Ticket</TableCell>
                    <TableCell>Service Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Client Rider ID</TableCell>
                    <TableCell>Vehicle ID</TableCell>
                    <TableCell>Opened</TableCell>
                    <TableCell>Closed</TableCell>
                    <TableCell align="right">Repair Cost</TableCell>
                    <TableCell align="right">Collected</TableCell>
                    {subTab === 1 && <TableCell align="right">Pending Amount</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={subTab === 1 ? 11 : 10} align="center" sx={{ py: 5 }}>
                        <Typography color="text.secondary">
                          {error ? "Service tickets could not be loaded." : "No service tickets found."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : visibleRows.map((ticket) => (
                    <TableRow key={ticket.id ?? ticket.code} hover>
                      <TableCell>{ticket.code}</TableCell>
                      <TableCell>{ticket.serviceType.replace(/_/g, " ")}</TableCell>
                      <TableCell>
                        <Chip size="small" label={ticket.status.replace(/_/g, " ")} />
                      </TableCell>
                      <TableCell>{ticket.clientName || "-"}</TableCell>
                      <TableCell>{ticket.clientRiderID || "-"}</TableCell>
                      <TableCell sx={{ maxWidth: 180, overflowWrap: "anywhere" }}>
                        {ticket.vehicleId || "-"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(ticket.openedAt)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(ticket.closedAt)}</TableCell>
                      <TableCell align="right">{formatAmount(ticket.actualRepairCost)}</TableCell>
                      <TableCell align="right">{formatAmount(ticket.actualRepairCostCollected)}</TableCell>
                      {subTab === 1 && (
                        <TableCell align="right">
                          {ticket.actualRepairCost == null
                            ? "-"
                            : formatAmount(
                                Number(ticket.actualRepairCost) -
                                  Number(ticket.actualRepairCostCollected ?? 0)
                              )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>

      <Dialog
        open={createDialogOpen}
        onClose={closeCreateDialog}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={handleCreateTicket}>
          <DialogTitle>Raise Service Ticket</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              required
              fullWidth
              margin="dense"
              label="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              required
              fullWidth
              multiline
              minRows={3}
              margin="dense"
              label="Description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
            <Button
              component="label"
              variant="outlined"
              sx={{ mt: 1 }}
              disabled={creating}
            >
              Choose Image
              <input
                ref={imageInputRef}
                hidden
                type="file"
                accept="image/*"
                onChange={(event) => setImage(event.target.files?.[0] ?? null)}
              />
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {image?.name || "An image is required."}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeCreateDialog} disabled={creating}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={creating}>
              {creating ? <CircularProgress size={22} color="inherit" /> : "Submit Ticket"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => {
          if (!creating) setConfirmDialogOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirm Service Ticket</DialogTitle>
        <DialogContent>
          <Typography>
            Submit this service ticket for rider <strong>{riderId}</strong>?
          </Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Title:</strong> {title}
          </Typography>
          <Typography sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
            <strong>Description:</strong> {description}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Image:</strong> {image?.name}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={creating}>
            Go Back
          </Button>
          <Button
            variant="contained"
            onClick={submitConfirmedTicket}
            disabled={creating}
          >
            {creating ? <CircularProgress size={22} color="inherit" /> : "Confirm & Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RiderServiceTicketsTab;
